import React, { FC } from 'react';

import { ComponentsType, mqMin, theme, variant, LucidTheme } from '@lucid/theme';
import styled from '@emotion/styled';

import Box from '../box/box';
import Heading from '../heading/heading';
import Image from '../image/image';
import Link, { LinkProps } from '../link/link';
import Text from '../text/text';
import { ComponentImage, ComponentLink } from '../../types/generics';
import FeatureGridItem from './feature-grid-item';

export interface FeatureGridItem {
  /** The CTA for each `FeatureGridItem`. */
  cta?: ComponentLink;
  /** The description for each `FeatureGridItem`. */
  description: string;
  /** The unique key for each `FeatureGridItem`. */
  id: number | string;
  /** The Image for each `FeatureGridItem`. */
  image?: ComponentImage;
  /** The title for each `FeatureGridItem`. */
  title: string;
}

interface FeatureGridLinkProps extends Omit<LinkProps, 'to'>{
  primary?: boolean;
  secondary?: boolean;
  iconColor?: string;
}

interface MasonryFeatureGridProps {
  variant: 'masonry';
}

interface NonMasonryFeatureGridProps {
  /** Amount of columns to render on screens tablet-sized and above */
  columns?: 2 | 3 | 4;
  variant?: 'default' | 'offset' | 'cards';
}

export type FeatureGridProps = {
  /** Classname to apply to the component */
  className?: string;
  /** Items to be rendered inside the feature grid */
  items: FeatureGridItem[];
  /** Modified props for the Link **/
  linkProps?: FeatureGridLinkProps;
  /** Optional Title above grid */
  sectionTitle?: string;
  /** Optional Subtitle above grid */
  sectionSubtitle?: string;
  /** Feature Grid Layout Variant */
  variant?: ComponentsType['FeatureGrid'];
} & (MasonryFeatureGridProps | NonMasonryFeatureGridProps);

type FeatureGridContainerProps = Pick<NonMasonryFeatureGridProps, 'columns'> & Pick<FeatureGridProps, 'variant'>;

const FeatureGridContainer = styled(Box)(
  variant({
    scale: 'featureGrid',
    variants: {
      defaultVariant: {}
    }
  }),
  {
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: theme.space.s64,
    [mqMin.tabletSmall]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      columnGap: theme.space.s40,
    }
  },
  ({ columns }: FeatureGridContainerProps) => [
    columns && {
      [mqMin.tabletSmall]: {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        columnGap: columns === 2 ? theme.space.s104 : theme.space.s40
      },
    }
  ],
);

const FeatureGrid: FC<FeatureGridProps> = ({
  className,
  items,
  linkProps,
  sectionSubtitle,
  sectionTitle,
  variant = 'default',
  ...rest
}) => (
  <Box as="section" className={className}>
    {sectionTitle && (
      <Heading
        maxWidth={{ default: '100%', tablet: '50%' }}
        mb={sectionSubtitle ? 4 : 10}
        mx="auto"
        textAlign="center"
        variant="headline3XL"
      >
        {sectionTitle}
      </Heading>
    )}
    {sectionSubtitle && (
      <Text
        maxWidth={{ default: '100%', tablet: '50%' }}
        mb={10}
        mx="auto"
        textAlign="center"
        variant="paragraphS400"
      >
        {sectionSubtitle}
      </Text>
    )}
    <FeatureGridContainer
      {...rest}
      className="feature-grid__container"
      variant={variant}
    >
      {
        items.map((
          {
            description,
            id,
            title,
            cta,
            image
          },
          index
        ) => (
          <FeatureGridItem
            key={`${id}-${index + 1}`}
            variant={variant}
          >
            {image && (
              variant ==='masonry' ? (
                <Image
                  alt={image.imageAlt}
                  aspectRatio="4:3"
                  src={image.imageSrc}
                />
              ) : (
                <img alt={image.imageAlt} css={(theme: LucidTheme) => ({ marginBottom: theme.space.s24 })} src={image.imageSrc} />
              )
            )}
            <Box>
              <Heading
                as="h5"
                variant={variant === 'masonry' ? 'headlineXL' : 'headlineL'}
              >
                {title}
              </Heading>
              {description && (
                <Text variant="paragraphS400">
                  {description}
                </Text>
              )}
              {cta && (
                <Link
                  {...linkProps}
                  hasArrow
                  to={cta.linkUrl}
                  variant={linkProps?.secondary ? 'secondary' : 'primary'}
                >
                  {cta.linkText}
                </Link>
              )}
            </Box>
          </FeatureGridItem>
        ))
      }
    </FeatureGridContainer>
  </Box>
);

export default FeatureGrid;
export { FeatureGrid };
